import { BoxContainer, BoxContainerProps, CircleIcon, FluidCanvas, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "./WorkBox.module.css";
import styles from "./PlayerWorkBox.module.css";
import React from "react";
import clsx from "clsx";

export interface PlayerWorkBoxProps {
  children: React.ReactNode;
  label: string;
  labelInfo?: string;
  overlayMode?: BoxContainerProps["mode"];
  overlayContent?: React.ReactNode;
  extraHeaderContent?: React.ReactNode;
  className?: string;
  topBarClassName?: string;
  audio?: boolean;
  video?: boolean;
  isMe?: boolean;
  isHighlighted?: boolean;
  isPlayingSound?: boolean;
  nativeProps?: React.HTMLAttributes<HTMLDivElement>;
  idleAnimation?: boolean;
  onChangeAudio?: () => void;
  onChangeVideo?: () => void;
}


export const PlayerWorkBox = React.memo(({
  children,
  label,
  labelInfo,
  overlayMode,
  overlayContent,
  extraHeaderContent,
  className,
  topBarClassName,
  audio,
  video,
  isMe,
  isHighlighted,
  isPlayingSound,
  nativeProps,
  idleAnimation,
  onChangeAudio,
  onChangeVideo,
}: PlayerWorkBoxProps) => {
  const left = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (extraHeaderContent) {
      result.push(extraHeaderContent);
    }
    if (isHighlighted) {
      result.push(<ReactIcons.Hand className={styles.nameIcon} />);
    } else if (isMe) {
      result.push(<ReactIcons.Home className={styles.nameIcon} />);
    }
    result.push(<span key="label" className={workBoxStyles.label}>{label}</span>);
    if (labelInfo) {
      result.push(
        <span key="youLabel" className={workBoxStyles.labelInfo}>{labelInfo}</span>
      );
    }
    return result;
  }, [extraHeaderContent, isHighlighted, isMe, label, labelInfo]);

  const right = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (video !== undefined) {
      result.push(
        <PrimaryButton 
          key="video" 
          onClick={onChangeVideo}
        >
          <CircleIcon.Variants.Camera
            style={{ fontSize: "0.75em" }}
            disabled={!video}
          />
        </PrimaryButton>
      );
    }
    if (audio !== undefined) {
      result.push(
        <PrimaryButton
          key="audio"
          onClick={onChangeAudio}
        >
          <CircleIcon.Variants.Mic
            style={{ fontSize: "0.75em" }}
            disabled={!audio}
          />
        </PrimaryButton>
      );
    }
    return result;
  }, [video, audio, onChangeVideo, onChangeAudio]);

  const renderIdleAnimation = () => {
    if (!idleAnimation) return null;
    return (
      <div className={styles.idleAnimationContainer}>
        <FluidCanvas speed={0.5} shapeSize="big" />
      </div>
    );
  };
  
  return (
    <BoxContainer
      className={clsx({
        [styles.container]: true,
        [styles.highlighted]: isHighlighted,
        [styles.playingSound]: isPlayingSound,
      }, className)}
      topBarProps={{
        left,
        right: right.length ? right : undefined,
        className: clsx(styles.playerTopBar, topBarClassName),
      }}
      mode={overlayMode}
      overlayContent={overlayContent}
      nativeProps={nativeProps}
    >
      {children}
      {renderIdleAnimation()}
    </BoxContainer>
  );
});